import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, combineLatest, concatMap, EMPTY, filter, from, map, of, pipe, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { AuthService } from '../../oidc/auth.service';
import { LOGGER } from '../../oidc/shared/logger';
import { StorageActions } from '../../oidc/store/storage.actions';
import { reconcileHomescreen } from './homescreen-catalogue';
// LEGACY-HOMESCREEN-V1
import { convertLegacyHomescreen, isLegacyHomescreen } from './homescreen-legacy';
import { SettingsActions, SettingsApiActions } from './settings.actions';
import { settingsFeature } from './settings.feature';
import { HomescreenSettings } from './settings.models';
import { SettingsService } from './settings.service';
import { HttpErrorResponse } from '@angular/common/http';
import { readBootstrapRevision } from 'src/app/pages/wizard/wizard-bootstrap';
import { CURRENT_REVISION } from 'src/app/pages/wizard/wizard-catalogue';
import { AppStateService } from 'src/app/oidc/shared/app-state.service';
import { WizardPage } from 'src/app/pages/wizard/wizard.page';
import { ModalController } from '@ionic/angular';
import { TOKEN_STORAGE } from 'src/app/oidc/token-storage';

/**
 * The backend sends the value either as an object or as a JSON string. Null means the
 * payload could not be read, and the caller then keeps the local homescreen instead of
 * replacing it with something empty.
 *
 * A map in which every tile is hidden is a valid user choice, not a broken payload.
 */
const toHomescreenSettings = (value: unknown): HomescreenSettings | null => {
  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
    ? (parsed as HomescreenSettings)
    : null;
};

@Injectable()
export class SettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly settingsService = inject(SettingsService);
  private readonly auth = inject(AuthService);
  private readonly roles$ = toObservable(this.auth.userRoles);
  private readonly authenticated$ = toObservable(this.auth.isAuthenticated);
  private readonly log = inject(LOGGER).scope('settings');
  private readonly appState = inject(AppStateService);
  private readonly tokens = inject(TOKEN_STORAGE);
  private readonly modalCtrl = inject(ModalController);


  private describeError(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      return `HTTP ${e.status} ${e.statusText} for ${e.url ?? 'unbekannter URL'}: ${e.message}`;
    }
    if (e instanceof Error) return `${e.name}: ${e.message}`;
    if (typeof e === 'string') return e;
    try {
      return JSON.stringify(e);
    } catch {
      return Object.prototype.toString.call(e);
    }
  }

  /**
   * Runs once per app start, on `hydrated` because that is when the recorded revision is
   * known — and, for an installation that has none, when it can be told from the payloads
   * whether there was ever anything to record.
   */
  readonly start$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StorageActions.hydrated),
      take(1),
      concatMap(({ slices }) => this.startupActions(slices)),
      // Emitted one by one and in order, so `bootstrapped` has been reduced by the time
      // `started` reads `lastRevision`.
      concatMap(actions => from(actions)),
    ),
  );

  /**
   * Keeps the homescreen in step with the tiles the user is allowed to see: seeds them all
   * on a fresh install, adds the ones gained on login, drops the ones a lost role no longer
   * covers.
   *
   * Watches the homescreen as well as the roles, because a homescreen arriving from the
   * backend has not been checked against them. Feeding its own result back in is safe:
   * `reconcileHomescreen` reports null as soon as nothing is left to change, so the loop
   * settles after one round.
   *
   * `roles === null` means they cannot be determined at the moment. Skipping then is what
   * keeps a token refresh in flight from stripping tiles the user gets back seconds later.
   */
  readonly reconcileHomescreen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StorageActions.hydrated),
      take(1),
      switchMap(() =>
        combineLatest([this.roles$, this.store.select(settingsFeature.selectHomescreen)]).pipe(
          map(([roles, homescreen]) => {
            if (roles === null) {
              this.log.debug('skipping reconcile, roles not determined');
              return null;
            }
            const next = reconcileHomescreen(homescreen, roles);
            if (next) {
              this.log.info('homescreen reconciled', {
                roles,
                added: Object.keys(next).filter(id => !(id in homescreen)),
                removed: Object.keys(homescreen).filter(id => !(id in next)),
              });
            }
            return next;
          }),
          filter((homescreen): homescreen is HomescreenSettings => homescreen !== null),
          map(homescreen => SettingsActions.homescreenInitialized({ homescreen })),
        ),
      ),
    ),
  );

  /**
   * Background refresh that nothing waits for: the app is already usable from the stored
   * settings, and without a connection this just fails and leaves them alone.
   *
   * Runs only while someone is logged in — the endpoint needs a bearer token, so a fetch
   * without a session could only end in a 401. Watching `isAuthenticated` instead of
   * checking it once also covers a login that happens long after startup: every transition
   * into "authenticated" fetches again, and the `switchMap` drops a response that arrives
   * after the session it belongs to is gone.
   * 
   * Dispatches `homescreenInitialized`, never `homescreenUpdated` — the latter is what
   * `persistHomescreen$` uploads, so it would send the backend its own answer straight
   * back.
   */
  readonly fetchHomescreen$ = createEffect(() =>
    this.actions$.pipe(
      // Gated on `hydrated`, otherwise the meta-reducer would overwrite the fetched
      // result with the stored one a moment later.
      ofType(StorageActions.hydrated),
      take(1),
      switchMap(() => this.authenticated$),
      // Not a `filter`: the logout emission has to reach the switchMap so it can cancel
      // a fetch that is still in flight.
      switchMap(authenticated =>
        !authenticated ? EMPTY :
        this.settingsService.fetchSetting<unknown>('homescreen').pipe(
          // LEGACY-HOMESCREEN-V1: drop this branch together with `homescreen-legacy.ts`
          map(setting => ({
            version: setting.version,
            homescreen: isLegacyHomescreen(setting.version)
              ? convertLegacyHomescreen(setting.value)
              : toHomescreenSettings(setting.value),
          })),
          tap(({ version, homescreen }) =>
            this.log.debug('homescreen from backend', {
              version,
              // null means the payload was unreadable and the local state stays.
              tiles: homescreen ? Object.keys(homescreen).length : null,
            }),
          ),
          filter(({ homescreen }) => homescreen !== null),
          map(({ homescreen }) =>
            SettingsActions.homescreenInitialized({ homescreen: homescreen as HomescreenSettings }),
          ),
          catchError((e: unknown) => {
            this.log.warn('homescreen fetch failed, keeping the local one', { error: this.describeError(e) });
            return of(SettingsApiActions.homescreenFetchFailed({ reason: this.describeError(e) }));
          }),
        ),
      ),
    ),
  );

  /**
   * Uploads only what the user changed. `homescreenInitialized` carries what storage or
   * the backend already had, so uploading that would echo it back to its own source.
   *
   * `concatMap` keeps the uploads in order, so the backend ends up with the last state the
   * user actually produced.
   * 
   * Changing tiles is allowed while logged out — the arrangement then lives only on the
   * device. The local write happens through the settings persistence regardless of this
   * effect; skipped here is solely the upload, which without a session could only 401.
   * Note that such a local arrangement does not survive a later login: the fetch then
   * brings in the account's homescreen, which is the state that follows the user around.
   */
  readonly persistHomescreen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.homescreenUpdated),
      concatMap(({ homescreen }) => {
        // Checked when this update's turn in the queue comes, not when it was dispatched —
        // a change still queued when the session ends is kept local too.
        if (!this.auth.isAuthenticated()) {
          this.log.debug('homescreen changed while logged out, keeping it local');
          return EMPTY;
        }
        this.log.debug('uploading homescreen', { tiles: Object.keys(homescreen).length });
        return this.settingsService.uploadHomescreen(homescreen).pipe(
          map(() => SettingsApiActions.homescreenUploadSucceeded()),
          catchError((e: unknown) => {
            this.log.warn('homescreen upload failed', { error: String(e) });
            return of(SettingsApiActions.homescreenUploadFailed({ reason: String(e) }));
          }),
        );
      }),
    ),
  );

  /**
   * The absence of a payload is the signal, not the state's contents: a hydrated slice
   * that was never written looks exactly like a fresh install, and only the raw payloads
   * the action carries can tell the two apart.
   */
  private async startupActions(slices: Record<string, any>): Promise<readonly Action[]> {
    const actions: Action[] = [];
    if (slices[settingsFeature.name]?.wizard === undefined) {
      const lastRevision = await readBootstrapRevision();
      this.log.info('no wizard state yet, deriving where this installation starts', {
        lastRevision,
        freshInstall: lastRevision === null,
      });
      actions.push(SettingsActions.wizardBootstrapped({ lastRevision }));
    }
    actions.push(SettingsActions.wizardStarted());
    return actions;
  }

  private async present(): Promise<void> {
    /**
     * A pending login means this start is the return leg of an OAuth redirect: the app is
     * either being reloaded into the callback or waiting for the browser to come back.
     * Opening the wizard on top of that would interrupt a login the user just started —
     * and it costs nothing to wait, because the queue survives in storage.
     */
    if (await this.tokens.loadPendingLogin()) {
      this.log.debug('login in progress, the wizard waits for the next start');
      return;
    }

    const modal = await this.modalCtrl.create({
      component: WizardPage,
      // Not dismissible by accident: a step the user swiped away would count as shown.
      backdropDismiss: false,
    });
    await modal.present();
  }
  
  /**
   * Presenting is a side effect on the UI, so this dispatches nothing. It reads the queue
   * after `started` has filled it — dispatch is synchronous, so by the time the action
   * reaches here the reducer has already run.
   */
  readonly present$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.wizardStarted),
        withLatestFrom(this.store.select(pipe(settingsFeature.selectWizardPending))),
        tap(([, pending]) => this.log.info('wizard queue', { revision: CURRENT_REVISION, pending })),
        filter(([, pending]) => pending.length > 0),
        // A start out of the background would put the wizard behind a screen nobody is
        // looking at, and it would already count as shown.
        switchMap(() => this.appState.isActive$.pipe(filter(active => active), take(1))),
        concatMap(() => from(this.present())),
      ),
    { dispatch: false },
  );
}
